<template>
  <div class="tags-wrapper" :class="{ show: show }">
    <div class="tags-icon">
      <font-awesome-icon icon="tag" />
    </div>
    <div class="tags">
      <template v-for="tag in tagList">
        <template v-if="checkIfTagVisible(tag)">
          <div
            v-if="tag.label === 'Metric'"
            class="tag"
            v-b-tooltip:hover.html="
              `${tag.value.metricType}<br>${tag.value.metric}<br>${tag.value.aggregation}`
            "
            :key="`tag-${tag.value.metric}-${tag.value.aggregation}`"
          >
            <span>{{ tag.value.text }}</span>
            <div class="remove-tag" @click="removeTag(tag)">
              <font-awesome-icon icon="times" />
            </div>
          </div>
          <div
            v-else-if="tag.label === 'Social Class'"
            class="tag"
            :key="`tag-${tag.label}`"
          >
            <span>{{ tag.value }} income</span>
            <div class="remove-tag" @click="removeTag(tag)">
              <font-awesome-icon icon="times" />
            </div>
          </div>
          <div v-else class="tag" :key="`tag-${tag.label}-${tag.value}`">
            <span>{{ parseTagName(tag.value) }}</span>
            <div class="remove-tag" @click="removeTag(tag)">
              <font-awesome-icon icon="times" />
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import generalGetters from '../../store/modules/general/getterTypes'

export default {
  name: 'CurrentTags',
  props: {
    filters: Object,
    show: Boolean,
    removeAppliedFilter: Function,
    removeMetric: Function
  },
  data () {
    return {
      filtersCopy: {}
    }
  },
  computed: {
    ...mapGetters({
      userPermissions: generalGetters.GET_PERMISSIONS
    }),
    tagList () {
      const unpackedData = {}
      Object.keys(this.filters).forEach((key) => {
        if (this.currentSearchIsMetricBased || key !== 'metrics') {
          Object.keys(this.filters[key]).forEach((filterKey) => {
            unpackedData[`${key}.${filterKey}`] = this.filters[key][filterKey]
          })
        }
      })
      const tags = []
      let ageParsed = false
      let yearParsed = false
      Object.keys(unpackedData).forEach((key) => {
        if (key.includes('metricsList')) {
          unpackedData[key].forEach((metric, idx) => {
            tags.push({
              label: 'Metric',
              value: {
                text: `[${this.parseAggregationName(
                  metric.aggregation.value
                )}] ${this.parseMetricName(
                  metric.metric.value,
                  metric.metricType.value
                )}`,
                metricType: metric.metricType.value,
                metric: metric.metric.value,
                aggregation: metric.aggregation.value
              },
              key,
              idx
            })
          })
        } else if (key.includes('threshold')) {
          tags.push({
            label: 'Threshold',
            value: `${unpackedData[key].desc ? 'Best' : 'Worst'} ${
              unpackedData[key].amount
            } ${
              unpackedData[key].type
                ? this.filters.metrics.useConcepts &&
                  unpackedData[key].type === 'fragrances'
                  ? 'concepts'
                  : unpackedData[key].type
                : '-'
            }`,
            key
          })
        } else if (['startAge', 'endAge'].includes(key.split('.')[1])) {
          if (
            !ageParsed &&
            this.filters.demographics.startAge.value &&
            this.filters.demographics.endAge.value
          ) {
            tags.push({
              label: 'Age',
              value: `Age: ${this.filters.demographics.startAge.value} - ${this.filters.demographics.endAge.value}`,
              key: 'age'
            })
            ageParsed = true
          }
        } else if (['startYear', 'endYear'].includes(key.split('.')[1])) {
          if (
            !yearParsed &&
            this.filters.essentials.startYear.value &&
            this.filters.essentials.endYear.value
          ) {
            tags.push({
              label: 'Year',
              value: `Years: ${this.filters.essentials.startYear.value} - ${this.filters.essentials.endYear.value}`,
              key: 'year'
            })
            yearParsed = true
          }
        } else if (key.includes('verified')) {
          if (this.userPermissions.isAdmin) {
            tags.push({
              label: 'Not verified',
              value: `${this.filters.essentials.verified.value ? 'Verified' : 'Not verified'}`,
              key: 'verified'
            })
          }
        } else if (key.includes('creative')) {
          if (unpackedData[key].value !== undefined) {
            tags.push({
              label: 'Benchmark',
              value: `${this.filters.fragranceDetails.creative.value ? 'Creative' : 'Benchmark'}`,
              key: 'creative'
            })
          }
        } else {
          const value = unpackedData[key].value
          const label = unpackedData[key].label
          if (Array.isArray(value)) {
            value.forEach((el) => {
              tags.push({ label, value: el, key })
            })
          } else if (value) {
            tags.push({ label, value, key })
          }
        }
      })
      return tags
    },
    currentSearchIsMetricBased () {
      return ['analytics', 'cics'].includes(this.$route.name)
    }
  },
  methods: {
    removeTag (tag) {
      if (tag.key === 'metricsList') {
        this.removeMetric(tag.idx)
      } else {
        this.removeAppliedFilter(tag)
      }
    },
    isArray (x) {
      return Array.isArray(x)
    },
    parseAggregationName (value) {
      switch (value) {
        case 'top_box':
          return 'TB'
        case 'top_2_box':
          return 'T2B'
        case 'mean':
          return 'M'
        case 'eeg_mean':
          return 'EEG'
        case 'bottom_box':
          return 'BB'
        case 'bottom_2_box':
          return 'B2B'
        case 'just_right':
          return 'JAR/N'
        case 'variance':
          return 'VAR'
        case 'hi_yes':
          return 'HiY'
        case 'hi_no':
          return 'HiN'
        case 'irt_yes':
          return 'YES'
        case 'irt_no':
          return 'NO'
        default:
          return ''
      }
    },
    parseMetricName (value, type) {
      if (value === 'hfd_hedonic') {
        return 'Hedonics'
      }
      let data = value.split('_')
      if (type !== 'Performance') {
        data.splice(data.length - 1, 1)
      }
      return data.join(' ')
    },
    checkIfTagVisible (tag) {
      switch (tag.key.split('.')[1]) {
        case 'age':
          const { startAge, endAge } = this.filters.demographics
          return this.currentSearchIsMetricBased
            ? (startAge.value && startAge.value !== 0) ||
                (endAge.value && endAge.value !== 99)
            : false
        case 'threshold':
          return this.filters.metrics.threshold.applyThreshold
        case 'metricsList':
          return this.filters.metrics.metricsList.find(
            (el) =>
              el.metricType.value && el.metric.value && el.aggregation.value
          )
        case 'useConcepts':
          return false
        default:
          return !!tag.value
      }
    },
    parseTagName (value) {
      if (typeof value === 'string') {
        if (value === 'rdf' || value === 'eeg') {
          return value.toUpperCase()
        } else if (value === 'hfd_hedonic') {
          return 'hedonic'
        }
        return value
      } else if (typeof value === 'object') {
        if (value.text === 'rdf' || value.text === 'eeg') {
          return value.text.toUpperCase()
        } else if (value.text === 'hfd_hedonic') {
          return 'hedonic'
        }
        return value.text
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/scss/variables";
.tags-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;

  &.show {
    opacity: 1;
  }

  .tags-icon {
    color: $charcoal;
    font-size: 24px;
  }

  .tags {
    width: 100%;
    display: flex;
    flex-wrap: wrap;

    .tag {
      cursor: default;
      margin: 5px;
      padding: 0 5px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 24px;
      background: #f3f3f3 0% 0% no-repeat padding-box;
      border-radius: 4px;
      letter-spacing: 0;
      font-family: "Open Sans", sans-serif;
      font-weight: 600;
      font-size: 13px;
      line-height: 16px;
      color: $charcoal;

      .remove-tag {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-left: 6px;
        color: #9a9a9acc;
        width: 17px;
        height: 17px;
        cursor: pointer;
      }
    }
  }
}
</style>
